/**
 * Granting or revoking one capability across a selection — ROADMAP 9.12.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE EFFICIENT PATH, AND THE ONE THAT HAS TO ASK FIRST
 *
 * A row toggle is immediate: one visible row, one click to undo, and the
 * admin is looking straight at the thing they changed. That is deliberate —
 * a confirmation on every switch is what makes an admin screen slower than
 * psql, which is how it stops being used.
 *
 * Bulk is different in kind, not degree. It is N people's order path at once,
 * a mis-click is not visible per row, and the undo is another bulk action
 * against a selection that may have moved. So it confirms, and the dialog
 * says what is actually different about doing it now — which of the selected
 * traders is holding an open book — rather than asking "are you sure".
 *
 * ONE CAPABILITY AT A TIME, never "apply this whole form". A single button
 * that set all three would make the blast radius of one click three
 * capabilities wide, and it would overwrite values the admin never looked at.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { X } from 'lucide-react'
import { CAPABILITY_LABEL, EXECUTION_CAPABILITIES, type ExecutionCapability } from '@apna/shared'
import { Button } from '@/components/ui/button'

export function BulkBar({
  count,
  busy,
  onApply,
  onClear,
}: {
  count: number
  busy: boolean
  onApply: (capability: ExecutionCapability, on: boolean) => void
  onClear: () => void
}) {
  if (count === 0) return null

  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-primary/35 bg-primary/[0.05] px-3 py-2"
      data-testid="bulk-bar"
      role="group"
      aria-label="Apply a capability to the selected traders"
    >
      <span className="text-[12px] font-semibold tabular-nums">
        {count} selected
      </span>

      {EXECUTION_CAPABILITIES.map((capability) => (
        <div key={capability} className="flex items-center gap-1.5">
          <span className="text-[11.5px] text-muted-foreground">{CAPABILITY_LABEL[capability]}</span>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            data-testid={`bulk-${capability}-on`}
            onClick={() => onApply(capability, true)}
            className="h-6 px-2 text-[10px] font-bold uppercase tracking-wider"
          >
            On
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            data-testid={`bulk-${capability}-off`}
            onClick={() => onApply(capability, false)}
            className="h-6 px-2 text-[10px] font-bold uppercase tracking-wider"
          >
            Off
          </Button>
        </div>
      ))}

      <button
        type="button"
        onClick={onClear}
        disabled={busy}
        data-testid="bulk-clear"
        className="ml-auto flex items-center gap-1 text-[11.5px] text-muted-foreground hover:text-foreground disabled:opacity-50"
      >
        <X size={12} />
        Clear
      </button>
    </div>
  )
}
